import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { useAvailablePriceRange } from 'clientState/providers/ParsedSearchResponseProvider';
import { useOrderFilterTagsActions } from 'clientState/providers/OrderedFilterTagsProvider';
import { isNullOrUndefined } from 'clientState/utilsHelper';

const ALIAS = 'priceRange';
const PriceFilterContext = createContext([]);

const priceReducer = (selectedPriceRange, action) => {
  switch (action.type) {
    case 'RESET':
      return null;
    case 'FILTER':
      return action.values;
    default:
      return selectedPriceRange;
  }
};

export const useAppliedPriceFilter = () => {
  const availablePriceRange = useAvailablePriceRange();
  const [priceRange] = useContext(PriceFilterContext);
  return (
    !isNullOrUndefined(priceRange) &&
    !isNullOrUndefined(availablePriceRange) &&
    (priceRange[0] > availablePriceRange?.minPrice.amount ||
      priceRange[1] < availablePriceRange?.maxPrice.amount)
  );
};

export const usePriceFilter = () => {
  const availablePriceRange = useAvailablePriceRange();
  const actions = useOrderFilterTagsActions();
  const [priceRange, dispatch] = useContext(PriceFilterContext);
  const { add, remove, clearAllStartsWith } = actions;
  const minAvailablePriceRangeAmount = availablePriceRange?.minPrice.amount;
  const maxAvailablePriceRangeAmount = availablePriceRange?.maxPrice.amount;
  const applyFilter = useMemo(() => {
    return (values) => {
      dispatch({
        type: 'FILTER',
        values,
      });
      const alias = ALIAS;
      if (
        values[0] > minAvailablePriceRangeAmount ||
        values[1] < maxAvailablePriceRangeAmount
      ) {
        add({
          alias,
          undo: () => {
            dispatch({ type: 'RESET' });
            clearAllStartsWith(alias);
          },
        });
      } else {
        remove({ alias });
      }
    };
  }, [
    dispatch,
    minAvailablePriceRangeAmount,
    maxAvailablePriceRangeAmount,
    add,
    clearAllStartsWith,
    remove,
  ]);

  const resetFilter = useCallback(() => {
    dispatch({ type: 'RESET' });
    clearAllStartsWith(ALIAS);
  }, [dispatch, clearAllStartsWith]);
  return [priceRange, applyFilter, resetFilter];
};

const PriceFilterProvider = ({ children }) => {
  const stateAndDispatch = useReducer(priceReducer, null);
  return (
    <PriceFilterContext.Provider value={stateAndDispatch}>
      {children}
    </PriceFilterContext.Provider>
  );
};

export default PriceFilterProvider;
